import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const serviceTypes = ['HVAC', 'Plumbing', 'Roofing', 'Electrical', 'General Contracting', 'Other'];
const leadVolumes = ['Under 50 leads/month', '50–150 leads/month', '150–300 leads/month', '300–500 leads/month', '500+ leads/month'];
const nextSteps = [
  { icon: 'ri-search-eye-line', title: 'We review your current setup', desc: 'Our team analyzes your existing lead flow, response times, and booking process.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Identify missed revenue opportunities', desc: 'We pinpoint exactly where leads are falling through the cracks and quantify the loss.' },
  { icon: 'ri-settings-4-line', title: 'Map out your automation system', desc: 'We design a custom AI-powered workflow tailored to your trade and volume.' },
  { icon: 'ri-file-list-3-line', title: 'Provide a clear implementation plan', desc: 'You receive a step-by-step roadmap with timelines, deliverables, and expected outcomes.' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', businessName: '', email: '', phone: '', serviceType: '', leadVolume: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) { setError('Message must be 500 characters or fewer.'); return; }
    setError(''); setSubmitting(true);
    const body = new URLSearchParams({ name: formData.name, businessName: formData.businessName, email: formData.email, phone: formData.phone, serviceType: formData.serviceType, leadVolume: formData.leadVolume, message: formData.message });
    try {
      await fetch('https://readdy.ai/api/form/d79ubbbll6jbq2vddu10', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } catch { setError('Something went wrong. Please try again.'); } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-[#0F172A] text-sm placeholder-[#334155]/30 focus:outline-none focus:border-[#2563EB]/40 focus:bg-white transition-all duration-200 font-inter";
  const labelCls = "block text-[#334155]/70 text-xs font-semibold mb-2 font-inter";

  return (
    <div className="bg-[#F8FAFC] min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#060A14] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/40ce4e5ab082662c739ee4364aeba9a9.jpg"
            alt="Contact PureHomeScale"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/60 via-[#060A14]/50 to-[#060A14]"></div>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-[#2563EB]/12 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#06B6D4] text-xs font-semibold tracking-[0.2em] uppercase mb-6 font-inter">Contact Us</p>
          <h1 className="text-6xl md:text-7xl font-black text-white leading-[1.0] tracking-tight mb-5 font-poppins">
            Let&apos;s Build Your<br /><span className="text-[#F97316]">Growth System</span>
          </h1>
          <p className="text-white/45 text-xl max-w-xl mx-auto font-inter font-light">Book a strategy session and see how PureHomeScale can transform your operations.</p>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="flex-1 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-[#E2E8F0]/80 rounded-3xl p-8 md:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#ECFDF5] mb-6">
                      <i className="ri-check-line text-3xl text-[#22C55E]"></i>
                    </div>
                    <h3 className="text-[#0F172A] text-2xl font-black mb-3 font-poppins">You&apos;re on the list!</h3>
                    <p className="text-[#334155]/55 text-sm max-w-sm font-inter">We&apos;ve received your request. Our team will reach out within 1 business day to schedule your strategy call.</p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-[#0F172A] text-2xl font-black mb-1 font-poppins">Schedule Your Strategy Call</h2>
                    <p className="text-[#334155]/45 text-sm mb-8 font-inter">Fill out the form below and we&apos;ll be in touch shortly.</p>
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div><label className={labelCls}>Name <span className="text-[#F97316]">*</span></label><input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className={inputCls} /></div>
                        <div><label className={labelCls}>Business Name <span className="text-[#F97316]">*</span></label><input type="text" name="businessName" required value={formData.businessName} onChange={handleChange} placeholder="Smith HVAC Co." className={inputCls} /></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div><label className={labelCls}>Email <span className="text-[#F97316]">*</span></label><input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@smithhvac.com" className={inputCls} /></div>
                        <div><label className={labelCls}>Phone <span className="text-[#F97316]">*</span></label><input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000" className={inputCls} /></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className={labelCls}>Service Type <span className="text-[#F97316]">*</span></label>
                          <div className="relative">
                            <select name="serviceType" required value={formData.serviceType} onChange={handleChange} className={`${inputCls} appearance-none cursor-pointer`}>
                              <option value="" disabled>Select your trade</option>
                              {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#334155]/40"><i className="ri-arrow-down-s-line text-base"></i></div>
                          </div>
                        </div>
                        <div>
                          <label className={labelCls}>Monthly Lead Volume</label>
                          <div className="relative">
                            <select name="leadVolume" value={formData.leadVolume} onChange={handleChange} className={`${inputCls} appearance-none cursor-pointer`}>
                              <option value="" disabled>Estimate your volume</option>
                              {leadVolumes.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#334155]/40"><i className="ri-arrow-down-s-line text-base"></i></div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Message</label>
                        <textarea name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={4} placeholder="Tell us about your current challenges..." className={`${inputCls} resize-none`} />
                        <p className="text-[#334155]/30 text-xs mt-1 text-right font-inter">{formData.message.length}/500</p>
                      </div>
                      {error && <p className="text-red-500 text-sm font-inter">{error}</p>}
                      <button type="submit" disabled={submitting} className="w-full bg-[#0F172A] text-white font-semibold text-base py-4 rounded-full hover:bg-[#1e293b] transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2 font-inter">
                        {submitting ? <><i className="ri-loader-4-line animate-spin"></i>Sending...</> : <>Schedule Strategy Call <i className="ri-arrow-right-line"></i></>}
                      </button>
                      <p className="text-[#334155]/30 text-xs text-center font-inter">Free consultation. No commitment required.</p>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <h3 className="text-[#0F172A] text-xl font-black mb-6 font-poppins">What Happens Next</h3>
                <div className="space-y-5">
                  {nextSteps.map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
                        <i className={`${step.icon} text-[#334155]/50 text-base`}></i>
                      </div>
                      <div>
                        <p className="text-[#0F172A] text-sm font-semibold mb-1 font-poppins">{step.title}</p>
                        <p className="text-[#334155]/45 text-xs leading-relaxed font-inter">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#E2E8F0]"></div>

              <div>
                <h3 className="text-[#0F172A] text-xl font-black mb-5 font-poppins">Contact Info</h3>
                <div className="space-y-4">
                  <a href="mailto:support@purehomescale.com" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] group-hover:border-[#0F172A]/20 transition-colors">
                      <i className="ri-mail-line text-[#334155]/40 text-sm"></i>
                    </div>
                    <span className="text-[#334155]/55 text-sm group-hover:text-[#0F172A] transition-colors font-inter">support@purehomescale.com</span>
                  </a>
                  <a href="tel:+10000000000" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] group-hover:border-[#0F172A]/20 transition-colors">
                      <i className="ri-phone-line text-[#334155]/40 text-sm"></i>
                    </div>
                    <span className="text-[#334155]/55 text-sm group-hover:text-[#0F172A] transition-colors font-inter">(XXX) XXX-XXXX</span>
                  </a>
                </div>
              </div>

              <div className="border-t border-[#E2E8F0]"></div>

              <div className="bg-[#0F172A] rounded-2xl p-6">
                <i className="ri-double-quotes-l text-[#F97316] text-2xl mb-3 block"></i>
                <p className="text-white/55 text-sm leading-relaxed font-inter">PureHomeScale is built for operators who want to scale without adding complexity. If you&apos;re ready to stop missing opportunities and start capturing more revenue, we&apos;re ready to help.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
